import { TRIAL_DAYS } from "@gf/shared";
import { Button, Callout, Field, Input } from "@gf/ui";
import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { ApiError } from "../../lib/api";
import { safeNext, useAuth } from "../../lib/auth";
import { AuthHeading, AuthLayout } from "./AuthLayout";
import { PasswordInput } from "./PasswordInput";
import { UpgradeOffer } from './UpgradeOffer';

function loginErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401 || err.code === "invalid_credentials") return "That email and password don't match. Try again.";
    if (err.code === "suspended" || (err.status === 403 && /suspend/i.test(err.message)))
      return err.message || "This account is suspended. Talk to whoever set it up for you.";
    if (err.status === 429) return "Too many attempts. Wait a few minutes, then try again.";
    return err.message;
  }
  return "Something went wrong. Try again.";
}

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const prefill = (location.state as { email?: string } | null)?.email ?? "";
  const [email, setEmail] = useState(prefill);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [upgradeToken,setUpgradeToken] = useState(()=>new URLSearchParams(location.hash.slice(1)).get('upgrade'));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError("Enter your email and password.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
      navigate(safeNext(params.get("next")), { replace: true });
    } catch (err) {
      if(err instanceof ApiError && err.code==='signup_verification_required' && err.signupUrl) {
        const signupUrl=new URL(err.signupUrl,window.location.origin);
        if(signupUrl.origin===window.location.origin && signupUrl.pathname==='/signup') { window.location.assign(signupUrl.href); return; }
      }
      if(err instanceof ApiError && err.code==='upgrade_required' && err.upgradeUrl) {
        const offerUrl=new URL(err.upgradeUrl,window.location.origin);
        if(offerUrl.origin===window.location.origin) {
          setUpgradeToken(new URLSearchParams(offerUrl.hash.slice(1)).get('upgrade'));
        }
      }
      setError(loginErrorMessage(err));
      setBusy(false);
    }
  }

  if(upgradeToken)return <UpgradeOffer token={upgradeToken}/>;
  return (
    <AuthLayout title="Sign in">
      <AuthHeading title="Sign in" lede="Welcome back. The family's been busy." />
      <p>New to GramFather? <Link to="/signup">Start a {TRIAL_DAYS}-day free trial</Link></p>
      <form className="gfp-auth__form" onSubmit={onSubmit} noValidate>
        <Field label="Email">
          <Input
            type="email"
            autoComplete="username"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            size="lg"
            autoFocus
            placeholder="you@business.com"
          />
        </Field>
        <Field
          label="Password"
          labelAction={
            <Link className="gf-link" to="/forgot" state={{ email }}>
              Forgot password?
            </Link>
          }
        >
          <PasswordInput autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} size="lg" />
        </Field>
        {error ? <Callout tone="danger">{error}</Callout> : null}
        <Button type="submit" variant="primary" size="lg" block loading={busy}>
          Sign in
        </Button>
      </form>
      <p className="gfp-auth__aside">
        Got your account from a reseller? Use the email they set it up with. See plans and prices at{" "}
        <a className="gf-link" href="https://www.gramfather.com/#pricing" target="_blank" rel="noreferrer">
          gramfather.com
        </a>
        .
      </p>
    </AuthLayout>
  );
}
